import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    makeStyles,
    Paper,
    Typography,
} from '@material-ui/core';
import { doctorServices } from '../services';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #243061 0%, #1a69ad 60%, #24bade 100%)',
        padding: theme.spacing(2),
        zIndex: 1300,
        overflowY: 'auto',
    },
    card: {
        padding: theme.spacing(5, 4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: 460,
        width: '100%',
        borderRadius: 16,
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(4, 3),
        },
    },
    logo: {
        width: 64,
        marginBottom: theme.spacing(3),
    },
    iconCircle: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 40,
        marginBottom: theme.spacing(3),
    },
    iconSuccess: {
        backgroundColor: '#e6f9f0',
    },
    iconError: {
        backgroundColor: '#fff3e0',
    },
    iconWarning: {
        backgroundColor: '#fff8e1',
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
        color: '#243061',
    },
    subtitle: {
        color: '#666',
        lineHeight: 1.7,
        marginBottom: theme.spacing(1),
    },
    countdown: {
        color: '#1a69ad',
        fontWeight: 600,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    btnPrimary: {
        marginTop: theme.spacing(3),
        borderRadius: 100,
        padding: '12px 36px',
        backgroundColor: '#ffbf00',
        color: '#243061',
        fontWeight: 700,
        fontSize: '1rem',
        '&:hover': {
            backgroundColor: '#f0b000',
        },
    },
    btnSecondary: {
        marginTop: theme.spacing(1.5),
        borderRadius: 100,
        padding: '10px 32px',
        borderColor: '#243061',
        color: '#243061',
        fontWeight: 600,
    },
    loadingText: {
        color: '#555',
        marginTop: theme.spacing(2.5),
    },
    divider: {
        width: '100%',
        height: 1,
        backgroundColor: '#e8e8e8',
        margin: theme.spacing(3, 0, 2),
    },
    helpText: {
        fontSize: '0.8rem',
        color: '#999',
    },
}));

const CONTACT_EMAIL = 'contacto@unio.cl';

export default function VerifyEmail() {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    // 'loading' | 'success' | 'used' | 'no-token' | 'network'
    const [uiState, setUiState] = useState('loading');
    const [serverMessage, setServerMessage] = useState('');
    const [countdown, setCountdown] = useState(5);

    const runVerification = useCallback(async () => {
        setUiState('loading');

        const query = new URLSearchParams(location.search);
        const token = query.get('token');

        if (!token) {
            setUiState('no-token');
            return;
        }

        const { status, response } = await doctorServices.verifyEmail({ token });

        if (status === 200) {
            setUiState('success');
            return;
        }

        if (status === 0) {
            setUiState('network');
            return;
        }

        setServerMessage(response?.error || '');
        setUiState('used');
    }, [location.search]);

    useEffect(() => {
        runVerification();
    }, [runVerification]);

    useEffect(() => {
        if (uiState !== 'success') return;
        if (countdown === 0) {
            history.push('/iniciar-sesion');
            return;
        }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [uiState, countdown, history]);

    const goToLogin = () => history.push('/iniciar-sesion');
    const goToHome = () => history.push('/');

    return (
        <Box className={classes.root}>
            <Container maxWidth="sm" style={{ display: 'flex', justifyContent: 'center' }}>
                <Paper className={classes.card} elevation={4}>

                    <img
                        src="/assets/logo/logo-unio.svg"
                        alt="Unio"
                        className={classes.logo}
                    />

                    {uiState === 'loading' && <LoadingState classes={classes} />}
                    {uiState === 'success' && <SuccessState classes={classes} countdown={countdown} onLogin={goToLogin} />}
                    {uiState === 'used' && <UsedTokenState classes={classes} message={serverMessage} onLogin={goToLogin} />}
                    {uiState === 'no-token' && <NoTokenState classes={classes} onHome={goToHome} />}
                    {uiState === 'network' && <NetworkErrorState classes={classes} onRetry={runVerification} />}

                </Paper>
            </Container>
        </Box>
    );
}

function LoadingState({ classes }) {
    return (
        <>
            <CircularProgress size={56} style={{ color: '#1a69ad' }} />
            <Typography variant="h6" className={classes.loadingText}>
                Verificando tu correo...
            </Typography>
            <Typography variant="body2" style={{ color: '#aaa', marginTop: 8 }}>
                Esto solo toma un momento.
            </Typography>
        </>
    );
}

function SuccessState({ classes, countdown, onLogin }) {
    return (
        <>
            <Box className={`${classes.iconCircle} ${classes.iconSuccess}`}>✅</Box>
            <Typography variant="h5" className={classes.title}>
                ¡Tu correo fue verificado!
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
                Tu cuenta está activa. Ya podés iniciar sesión en Unio y acceder al botiquín virtual.
            </Typography>
            <Typography variant="body2" className={classes.countdown}>
                Redirigiendo al login en {countdown}...
            </Typography>
            <Button
                variant="contained"
                className={classes.btnPrimary}
                onClick={onLogin}
                disableElevation
            >
                Ir al Login ahora
            </Button>
        </>
    );
}

function UsedTokenState({ classes, message, onLogin }) {
    const isExpired = message?.toLowerCase().includes('expir');
    return (
        <>
            <Box className={`${classes.iconCircle} ${classes.iconWarning}`}>🔗</Box>
            <Typography variant="h5" className={classes.title}>
                Este link ya no es válido
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
                {isExpired
                    ? 'El link de verificación venció. Los links tienen una duración limitada por seguridad.'
                    : 'Este link ya fue utilizado. Si ya verificaste tu correo, podés iniciar sesión directamente.'}
            </Typography>
            <Button
                variant="contained"
                className={classes.btnPrimary}
                onClick={onLogin}
                disableElevation
            >
                Ir al Login
            </Button>
            <div className={classes.divider} />
            <Typography variant="body2" className={classes.helpText}>
                ¿Tenés problemas para acceder?{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#1a69ad' }}>
                    Contactá a soporte
                </a>
            </Typography>
        </>
    );
}

function NoTokenState({ classes, onHome }) {
    return (
        <>
            <Box className={`${classes.iconCircle} ${classes.iconError}`}>⚠️</Box>
            <Typography variant="h5" className={classes.title}>
                Link incompleto
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
                El link que usaste parece estar incompleto o roto. Por favor buscá el correo de verificación que te enviamos y hacé clic en el botón amarillo.
            </Typography>
            <Button
                variant="contained"
                className={classes.btnPrimary}
                onClick={onHome}
                disableElevation
            >
                Volver al inicio
            </Button>
            <div className={classes.divider} />
            <Typography variant="body2" className={classes.helpText}>
                ¿Necesitás que te reenvíen el correo?{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#1a69ad' }}>
                    Escribinos a {CONTACT_EMAIL}
                </a>
            </Typography>
        </>
    );
}

function NetworkErrorState({ classes, onRetry }) {
    return (
        <>
            <Box className={`${classes.iconCircle} ${classes.iconError}`}>📡</Box>
            <Typography variant="h5" className={classes.title}>
                Sin conexión
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
                No pudimos conectarnos con el servidor. Revisá tu conexión a internet e intentá de nuevo.
            </Typography>
            <Button
                variant="contained"
                className={classes.btnPrimary}
                onClick={onRetry}
                disableElevation
            >
                Intentar de nuevo
            </Button>
            <div className={classes.divider} />
            <Typography variant="body2" className={classes.helpText}>
                Si el problema persiste,{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#1a69ad' }}>
                    contactá a soporte
                </a>
            </Typography>
        </>
    );
}
